import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext'; // Add this import
import { useMutation, useQueryClient } from 'react-query';
import { authAPI } from '../services/api';
import { customToast, toastMessages } from '../utils/toast';
import {
  User,
  Mail,
  Building2,
  Edit2,
  Save,
  X,
  CheckCircle,
  Sparkles,
  Camera,
  TrendingUp,
  Users,
  QrCode,
  Package,
  Play
} from 'lucide-react';

const Profile = () => {
  const { user, updateProfile } = useAuth();
  const { t } = useLanguage(); // Add language context
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    restaurant_id: '',
    restaurant_name: '',
    created_at: ''
  });

  const isOwner = user?.role === 'owner';
  const isStaff = user?.role === 'staff';

  useEffect(() => {
    setIsVisible(true);
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        restaurant_id: user.restaurant_id || '',
        restaurant_name: user.restaurant_name || '',
        created_at: user.created_at || ''
      });
    }
  }, [user]);

  // Update profile mutation
  // In Profile.js, update the mutation onSuccess:
  const updateProfileMutation = useMutation(
    (data) => authAPI.updateProfile(data),
    {
      onSuccess: (response) => {
        // Use updateProfile from context instead of setUser
        if (response.data && response.data.user) {
          updateProfile(response.data.user);
        }

        queryClient.invalidateQueries('user');
        customToast.success(toastMessages.profileUpdated);
        setIsEditing(false);
      },
      onError: (error) => {
        customToast.error(toastMessages.profileError);
      },
    }
  );

  console.log("Submitting form data:", formData);


  const handleSubmit = (e) => {
    e.preventDefault();
    updateProfileMutation.mutate(formData);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const cancelEdit = () => {
    setFormData({
      name: user.name || '',
      email: user.email || '',
      restaurant_id: user.restaurant_id || '',
      restaurant_name: user.restaurant_name || '',
      created_at: user.created_at || ''
    });
    setIsEditing(false);
  };

  // Mock data for statistics - only for owners
  const stats = [
    { icon: Users, label: t('profile.stats.staffMembers', 'Staff Members'), value: '8', change: '+2' },
    { icon: QrCode, label: t('profile.stats.activeTables', 'Active Tables'), value: '12', change: '+3' },
    { icon: Package, label: t('profile.stats.menuItems', 'Menu Items'), value: '45', change: '+5' },
    { icon: TrendingUp, label: t('profile.stats.monthlyOrders', 'Monthly Orders'), value: '1.2K', change: '+18%' }
  ];




  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="loading"></div>
      </div>
    );
  }

  // Navigation items based on role
  const navigationItems = [
    { id: 'profile', label: t('profile.navigation.profile', 'Profile Information'), icon: User }
  ];

  if (isOwner) {
    navigationItems.push(
      { id: 'restaurant', label: t('profile.navigation.restaurant', 'Restaurant Details'), icon: Building2 },
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 p-4">
      {/* Animated Background */}
      <div className="absolute inset-0 -z-10 h-full w-full bg-white bg-[linear-gradient(to_right,#f0f0f0_1px,transparent_1px),linear-gradient(to_bottom,#f0f0f0_1px,transparent_1px)] bg-[size:6rem_4rem]">
        <div className="absolute bottom-0 left-0 right-0 top-0 bg-[radial-gradient(circle_800px_at_50%_100px,#f0f9ff,transparent)]"></div>
      </div>

      <div className="container mx-auto px-4 py-6 md:py-8">
        {/* Header */}
        <div className={`mb-6 md:mb-8 transition-all duration-700 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
          <div className="flex items-center gap-3 mb-2">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
              <Sparkles className="h-4 w-4" />
              {isOwner
                ? t('profile.header.ownerTitle', 'Profile Management')
                : t('profile.header.staffTitle', 'My Profile')
              }
            </div>
          </div>
          <h1 className="text-2xl md:text-4xl font-bold text-gray-900 mb-2 md:mb-3">
            {isOwner
              ? t('profile.header.ownerHeading', 'Your Profile')
              : t('profile.header.staffHeading', 'My Profile')
            }
          </h1>
          <p className="text-gray-600 text-sm md:text-lg max-w-2xl">
            {isOwner
              ? t('profile.header.ownerDescription', 'Manage your account settings, restaurant information, and subscription details.')
              : t('profile.header.staffDescription', 'Manage your personal information and account settings.')
            }
          </p>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-4 gap-4 md:gap-8">
          {/* Sidebar - Only show if owner, simplified for staff */}
          {isOwner ? (
            <div className="xl:col-span-1">
              <div className={`bg-white rounded-2xl shadow-lg border border-gray-100 p-4 md:p-6 transition-all duration-700 delay-200 ${isVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'
                }`}>
                {/* User Card */}
                <div className="text-center mb-6 md:mb-8">
                  <div className="relative inline-block mb-3 md:mb-4">
                    <div className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-2xl flex items-center justify-center text-white text-xl md:text-2xl font-bold mx-auto">
                      {user.name?.charAt(0) || user.restaurant_name?.charAt(0) || 'U'}
                    </div>
                  </div>
                  <h3 className="font-bold text-gray-900 text-base md:text-lg mb-1">
                    {user.name || t('profile.user.defaultName', 'User')}
                  </h3>
                  <p className="text-gray-600 text-xs md:text-sm mb-2 md:mb-3">{user.email}</p>
                  <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 px-2 py-1 md:px-3 md:py-1 rounded-full text-xs font-medium">
                    <CheckCircle className="h-3 w-3" />
                    {user.role === 'owner'
                      ? t('profile.role.owner', 'Restaurant Owner')
                      : t('profile.role.staff', 'Staff Member')
                    }
                  </div>
                </div>

                {/* Navigation */}
                <nav className="space-y-1 md:space-y-2">
                  {navigationItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.id}
                        onClick={() => setActiveTab(item.id)}
                        className={`w-full flex items-center gap-2 md:gap-3 px-3 py-2 md:px-4 md:py-3 rounded-xl text-left transition-all duration-300 text-sm md:text-base ${activeTab === item.id
                          ? 'bg-blue-50 text-blue-700 border border-blue-200'
                          : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                          }`}
                      >
                        <Icon className="h-4 w-4 md:h-5 md:w-5" />
                        <span className="font-medium">{item.label}</span>
                      </button>
                    );
                  })}
                </nav>
              </div>
            </div>
          ) : (
            // Simplified sidebar for staff
            <div className="xl:col-span-1">
              <div className={`bg-white rounded-2xl shadow-lg border border-gray-100 p-4 md:p-6 transition-all duration-700 delay-200 ${isVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'
                }`}>
                <div className="text-center mb-6 md:mb-8">
                  <div className="relative inline-block mb-3 md:mb-4">
                    <div className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-2xl flex items-center justify-center text-white text-xl md:text-2xl font-bold mx-auto">
                      {user.name?.charAt(0) || 'U'}
                    </div>
                  </div>
                  <h3 className="font-bold text-gray-900 text-base md:text-lg mb-1">
                    {user.name || t('profile.staff.defaultName', 'Staff Member')}
                  </h3>
                  <p className="text-gray-600 text-xs md:text-sm mb-2 md:mb-3">{user.email}</p>
                  <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-2 py-1 md:px-3 md:py-1 rounded-full text-xs font-medium">
                    <Users className="h-3 w-3" />
                    {t('profile.role.staff', 'Staff Member')}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Main Content */}
          <div className={isOwner ? "xl:col-span-3" : "xl:col-span-4"}>
            {/* Profile Information Tab - Available for both roles */}
            {activeTab === 'profile' && (
              <div className={`bg-white rounded-2xl shadow-lg border border-gray-100 transition-all duration-700 delay-400 ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'
                }`}>
                <div className="p-4 md:p-6 border-b border-gray-100">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                      <h2 className="text-xl md:text-2xl font-bold text-gray-900">
                        {t('profile.tabs.profile.title', 'Profile Information')}
                      </h2>
                      <p className="text-gray-600 mt-1 text-sm md:text-base">
                        {isOwner
                          ? t('profile.tabs.profile.ownerDescription', 'Update your personal information and contact details')
                          : t('profile.tabs.profile.staffDescription', 'Update your personal information')
                        }
                      </p>
                    </div>
                    {!isEditing ? (
                      <button
                        onClick={() => setIsEditing(true)}
                        className="flex items-center justify-center gap-2 bg-blue-600 text-white px-3 md:px-4 py-2 rounded-xl font-medium hover:bg-blue-700 transition-all duration-300 hover:scale-105 text-sm md:text-base whitespace-nowrap"
                      >
                        <Edit2 className="h-4 w-4" />
                        {t('profile.actions.editProfile', 'Edit Profile')}
                      </button>
                    ) : (
                      <div className="flex gap-2">
                        <button
                          onClick={cancelEdit}
                          className="flex items-center justify-center gap-2 bg-gray-100 text-gray-700 px-3 md:px-4 py-2 rounded-xl font-medium hover:bg-gray-200 transition-all duration-300 text-sm md:text-base"
                        >
                          <X className="h-4 w-4" />
                          {t('profile.actions.cancel', 'Cancel')}
                        </button>
                        <button
                          onClick={handleSubmit}
                          disabled={updateProfileMutation.isLoading}
                          className="flex items-center justify-center gap-2 bg-green-600 text-white px-3 md:px-4 py-2 rounded-xl font-medium hover:bg-green-700 transition-all duration-300 hover:scale-105 disabled:opacity-50 text-sm md:text-base"
                        >
                          <Save className="h-4 w-4" />
                          {updateProfileMutation.isLoading
                            ? t('profile.actions.saving', 'Saving...')
                            : t('profile.actions.save', 'Save')
                          }
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="p-4 md:p-6">
                  <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700">
                        {t('profile.form.fullName', 'Full Name')}
                      </label>
                      {isEditing ? (
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          className="w-full px-3 md:px-4 py-2.5 md:py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-sm md:text-base"
                          placeholder={t('profile.form.namePlaceholder', 'Enter your full name')}
                        />
                      ) : (
                        <div className="px-3 md:px-4 py-2.5 md:py-3 bg-gray-50 rounded-xl text-gray-900 text-sm md:text-base">
                          {user.name || t('profile.form.notSet', 'Not set')}
                        </div>
                      )}
                    </div>

                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700">
                        {t('profile.form.email', 'Email Address')}
                      </label>
                      {isEditing ? (
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full px-3 md:px-4 py-2.5 md:py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-sm md:text-base"
                          placeholder={t('profile.form.emailPlaceholder', 'Enter your email')}
                        />
                      ) : (
                        <div className="px-3 md:px-4 py-2.5 md:py-3 bg-gray-50 rounded-xl text-gray-900 flex items-center gap-2 text-sm md:text-base">
                          <Mail className="h-4 w-4 text-gray-400" />
                          {user.email}
                        </div>
                      )}
                    </div>

                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700">
                        {t('profile.form.role', 'Role')}
                      </label>
                      <div className="px-3 md:px-4 py-2.5 md:py-3 bg-gray-50 rounded-xl text-gray-900 text-sm md:text-base capitalize">
                        {user.role || 'Restaurant'}
                      </div>
                    </div>

                    {/* Show restaurant info for staff if available */}
                    {isStaff && user.restaurant_name && (
                      <div className="space-y-1">
                        <label className="block text-sm font-medium text-gray-700">
                          {t('profile.form.restaurant', 'Restaurant')}
                        </label>
                        <div className="px-3 md:px-4 py-2.5 md:py-3 bg-gray-50 rounded-xl text-gray-900 flex items-center gap-2 text-sm md:text-base">
                          <Building2 className="h-4 w-4 text-gray-400" />
                          {user.restaurant_name}
                        </div>
                      </div>
                    )}

                    {isEditing && (
                      <div className="md:col-span-2 pt-4 border-t border-gray-100">
                        <p className="text-sm text-gray-500">
                          {t('profile.form.saveNotice', 'Make sure to save your changes before leaving this page.')}
                        </p>
                      </div>
                    )}
                  </form>
                </div>
              </div>
            )}

            {/* Restaurant Details Tab - Only for owners */}
            {isOwner && activeTab === 'restaurant' && (
              <div className={`bg-white rounded-2xl shadow-lg border border-gray-100 transition-all duration-700 delay-400 ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'
                }`}>
                <div className="p-4 md:p-6 border-b border-gray-100">
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900">
                    {t('profile.tabs.restaurant.title', 'Restaurant Details')}
                  </h2>
                  <p className="text-gray-600 mt-1 text-sm md:text-base">
                    {t('profile.tabs.restaurant.description', 'Manage your restaurant information and settings')}
                  </p>
                </div>

                <div className="p-4 md:p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700">
                        {t('profile.form.restaurantName', 'Restaurant Name')}
                      </label>
                      <div className="px-3 md:px-4 py-2.5 md:py-3 bg-gray-50 rounded-xl text-gray-900 flex items-center gap-2 text-sm md:text-base">
                        <Building2 className="h-4 w-4 text-gray-400" />
                        {user.restaurant_name || t('profile.form.notSet', 'Not set')}
                      </div>
                    </div>

                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700">
                        {t('profile.form.email', 'Email Address')}
                      </label>
                      <div className="px-3 md:px-4 py-2.5 md:py-3 bg-gray-50 rounded-xl text-gray-900 flex items-center gap-2 text-sm md:text-base">
                        <Mail className="h-4 w-4 text-gray-400" />
                        {user.email}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700">
                        {t('profile.form.RestId', 'Invitation Code')}
                      </label>
                      <div className="px-3 md:px-4 py-2.5 md:py-3 bg-gray-50 rounded-xl text-gray-900 flex items-center gap-2 text-sm md:text-base">
                        <Play className="h-4 w-4 text-gray-400" />
                        {user.restaurant_id}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;